import {createFileRoute} from '@tanstack/react-router';

import {BatchResponseEntryKind} from '@api/model';

import {parseIsoDay, parseNonEmpty} from '@/lib/search-params';
import {FILTER_KINDS} from '@/modules/journal-entries/lib/queue';
import {JournalEntryPushPage} from '@/modules/journal-entries/pages/JournalEntryPushPage';

/**
 * URL state for the journal-entry push queue.
 *
 * All optional so a link from elsewhere can land here bare; the page defaults
 * to reimbursements for the current month. `batch` is which entry is open in
 * the right-hand pane -- in the URL rather than component state so a reviewer
 * can send a colleague the exact voucher they are looking at, which is the
 * thing that actually gets asked about in a finance queue.
 */
export interface JournalEntrySearch {
  /** Inclusive period bounds (`yyyy-MM-dd`), windowed on the SETTLED date. */
  from?: string;
  to?: string;
  /** The open batch's id. */
  batch?: string;
  /**
   * `history` swaps the queue for the post history. In the URL rather than
   * component state for the same reason `batch` is: "what did we post in
   * July" is a thing one person sends another, and a reload should land back
   * on the history rather than on this month's queue.
   *
   * Typed as the one value it can hold rather than as `string`: the page
   * compares it against `'history'` and nothing else, so anything wider is a
   * set of states the page does not actually have.
   */
  view?: 'history';
  /**
   * Which tab (DERP-288). `verified` (advances closed), `unproven` (failure
   * of proof), or `amended` -- posted entries whose claim was amended after
   * posting, to send the correction to SQL Account. Absent is **Payout**
   * (reimbursements paid and advances paid out), so that one is never written.
   */
  tab?: 'verified' | 'unproven' | 'amended';
  /**
   * The queue's Type filter (`FILTER_KINDS`). Absent is every kind. In the
   * URL with the tab it narrows, so a reload or a shared link lands on the
   * same rows, and a tab change drops it.
   */
  type?: BatchResponseEntryKind;
}

/**
 * What the page shows, read once from the URL: one stage's queue, the Amended
 * tab, or the post history.
 *
 * Two params encode it, and they can disagree (`view=history&tab=amended`):
 * the history wins, and the tab is kept underneath it so "Back to queue"
 * returns to the tab the reviewer left. Deciding that here means the page
 * switches on one value instead of re-checking both flags at every branch.
 */
export type JournalEntryView =
  | {
      kind: 'queue';
      tab: 'payout' | 'verified' | 'unproven';
      /** The Type filter, only ever a kind this tab's filter holds. */
      type: BatchResponseEntryKind | undefined;
    }
  | {kind: 'amended'}
  | {kind: 'history'};

export const journalEntryView = (search: JournalEntrySearch): JournalEntryView => {
  if (search.view === 'history') return {kind: 'history'};
  if (search.tab === 'amended') return {kind: 'amended'};
  const tab = search.tab ?? 'payout';
  // A type the tab does not hold (a pasted `tab=verified&type=reimbursement`)
  // filters nothing, rather than emptying the queue.
  return {kind: 'queue', tab, type: FILTER_KINDS[tab]?.find((type) => type === search.type)};
};

/**
 * Exported so the URL contract is unit-testable: `renderWithProviders`
 * registers a test route at `path: initialPath`, so a query string there never
 * matches and search params cannot be driven through a rendered page. Mirrors
 * `routes/_authenticated/advances/ledger.tsx`.
 */
export const validateJournalEntrySearch = (
  search: Record<string, unknown>,
): JournalEntrySearch => ({
  from: parseIsoDay(search.from),
  to: parseIsoDay(search.to),
  batch: parseNonEmpty(search.batch),
  // Anything else drops to undefined, which is the queue -- an unknown view
  // in a pasted URL should land somewhere real rather than on a blank mode.
  view: search.view === 'history' ? 'history' : undefined,
  tab:
    search.tab === 'verified' || search.tab === 'unproven' || search.tab === 'amended'
      ? search.tab
      : undefined,
  type: Object.values(BatchResponseEntryKind).find((type) => type === search.type),
});

export const Route = createFileRoute('/_authenticated/journal-entries/')({
  validateSearch: validateJournalEntrySearch,
  component: JournalEntryPushPage,
});
